// Staff-side Network Test API client. Reads persisted reports the
// customer app has POSTed (via /v1/customer/net-test/report and
// flagged with sent_to_support = 1). The endpoints are gated at
// streamauth.RequireRole(admin, support).

import type { ApiClient } from "./client.js";

export interface NetTestReportListItem {
  id: string;
  username: string;
  overall_verdict: "pass" | "warn" | "fail" | "incomplete";
  package_snapshot: string;
  package_mbps: number;
  sent_to_support: boolean;
  resolved: "unresolved" | "in_progress" | "fixed" | "escalated" | "no_issue";
  agreement?: "agreed" | "disagreed" | "partial" | null;
  resolution_action?: string | null;
  claimed_by?: string | null;
  started_at: string;
  finished_at: string;
  created_at: string;
  top_cause?: string | null;
}

export interface NetTestReportListResponse {
  ok: true;
  reports: NetTestReportListItem[];
}

export interface NetTestReportDetailResponse {
  ok: true;
  id: string;
  username: string;
  overall_verdict: NetTestReportListItem["overall_verdict"];
  package_snapshot: string;
  package_mbps: number;
  sent_to_support: boolean;
  resolved: NetTestReportListItem["resolved"];
  agreement?: NetTestReportListItem["agreement"];
  resolution_action?: string | null;
  support_notes?: string | null;
  claimed_by?: string | null;
  started_at: string;
  finished_at: string;
  created_at: string;
  stages: Record<string, unknown> | null;
  env: Record<string, unknown> | null;
  intake: Record<string, unknown> | null;
  diagnosis: Record<string, unknown> | null;
}

export function makeNetTestApi(client: ApiClient) {
  return {
    /** List reports for a subscriber, newest first. */
    listReports(username: string, limit = 20) {
      return client.get<NetTestReportListResponse>("/v1/api/net-test/reports", {
        username,
        limit: String(limit),
      });
    },
    /** List UNCLAIMED reports across every subscriber — the NOC queue view. */
    listUnclaimed(limit = 50) {
      return client.get<NetTestReportListResponse>("/v1/api/net-test/reports", {
        unclaimed: "1",
        limit: String(limit),
      });
    },
    /** Full detail including stages / env / diagnosis JSON blobs. */
    getReport(id: string) {
      return client.get<NetTestReportDetailResponse>(`/v1/api/net-test/reports/${id}`);
    },
  };
}

export type NetTestApi = ReturnType<typeof makeNetTestApi>;
